import React, { useState } from 'react';
import { Bold, FormPageContainer, TextBlock } from '../forms';
import { Body2, Button, H1 } from '../../components/common';
import { useMessageGetter } from 'react-message-context';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormRow, Row } from '../forms/layoutComponents';
import { ButtonContainer } from '../layoutComponents';
import { DefectNotice, DefectNoticeAffectedArea, DefectNoticeClass, defectNoticeSchema } from './schema';
import { FileInput, FileInputValue, InputField, TextAreaElement } from '../../components/common/forms';
import { FlexColumn } from '../../components/basic/Flex';
import { RadioGroup } from '../../components/common/RadioGroup';
import { Dropdown, DropdownOptionValue } from '../../components/common/Dropdown';
import FormError from '../../components/common/forms/FormError';
import { FileIcon, ThrashFilledIcon } from '../../assets';
import { showNotification } from '../../context/appActions';
import { NotificationType, useAppDispatch } from '../../context/appContext';
import { objectToFormData } from '../forms/form-utils';
import { createDefectNotice } from '../../apis_kenno';
import { scrollToTop } from '../../utils';
import InfoBox from '../../components/common/InfoBox';

const MAX_FILES = 3;

export const DefectNoticeForm: React.FC = () => {
  const appDispatch = useAppDispatch();
  const translationPrefix = 'forms.defectNotice';
  const t = useMessageGetter(translationPrefix);
  const tForms = useMessageGetter('forms');
  const tCommon = useMessageGetter('common');
  const [isBusy, setIsBusy] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const formMethods = useForm<DefectNotice>({
    resolver: zodResolver(defectNoticeSchema),
    defaultValues: { masterKeyAccessAllowed: false, hasPetsInApartment: false },
  });

  const handleSubmit = async (data: DefectNotice) => {
    setIsBusy(true);
    setSubmitFailed(false);

    // Convert to FormData to support file uploads
    const formData = objectToFormData(data);

    try {
      await createDefectNotice(formData);
      scrollToTop();
      setSubmitted(true);
    } catch (e) {
      console.error(e);
      setSubmitFailed(true);
    } finally {
      setIsBusy(false);
    }
  };

  const defectClasses = [
    { label: t('class.hvac'), value: DefectNoticeClass.HVAC },
    { label: t('class.electrical'), value: DefectNoticeClass.ELECTRICAL },
    { label: t('class.locks'), value: DefectNoticeClass.LOCKS },
    { label: t('class.repair'), value: DefectNoticeClass.REPAIR },
    { label: t('class.other'), value: DefectNoticeClass.OTHER },
  ];

  const affectedAreas = [
    { label: t('apartment'), value: DefectNoticeAffectedArea.APARTMENT },
    { label: t('publicSpaces'), value: DefectNoticeAffectedArea.PUBLIC_SPACES },
    { label: t('courtyard'), value: DefectNoticeAffectedArea.COURTYARD },
  ];

  const hasPetsInApartment = formMethods.watch('hasPetsInApartment');
  const attachments = formMethods.watch('attachments');
  const files = attachments ? Array.from(attachments) : [];

  const handleFileInputChange = async (newFiles: FileInputValue) => {
    if (!newFiles) return;

    const existingFiles = attachments ? Array.from(attachments) : [];
    const updatedFiles = [...existingFiles, ...Array.from(newFiles)];

    if (updatedFiles.length > MAX_FILES) {
      await showNotification(appDispatch, t('error.tooManyFiles', { maxFiles: MAX_FILES }), NotificationType.ERROR);
      return;
    }

    formMethods.setValue('attachments', updatedFiles as unknown as FileList);
  };

  const handleRemoveFile = (index: number) => {
    const updatedFiles = attachments ? Array.from(attachments) : [];
    updatedFiles.splice(index, 1);
    formMethods.setValue('attachments', updatedFiles as unknown as FileList);
  };

  return (
    <FormPageContainer<DefectNotice> formMethods={formMethods}>
      <H1>{t('formTitle')}</H1>
      {submitted ? (
        <InfoBox type={NotificationType.INFO} message={t('submitted')} />
      ) : (
        <>
          <Body2>{t('formDescription1')}</Body2>
          <Body2>
            <Bold>{t('formDescription2')}</Bold>
          </Body2>
          <Form onSubmit={formMethods.handleSubmit(handleSubmit)}>
            <FormRow>
              <InputField<DefectNotice, string>
                translationPrefix={translationPrefix}
                fieldPath="title"
                type="text"
                required
              />
            </FormRow>
            <FormRow>
              <InputField<DefectNotice, DropdownOptionValue>
                fieldPath="defectClass"
                translationPrefix={translationPrefix}
                required
                inputElementRenderer={(props) => <Dropdown {...props} options={defectClasses} />}
              />
            </FormRow>
            <FormRow>
              <InputField<DefectNotice, DropdownOptionValue>
                fieldPath="affectedArea"
                translationPrefix={translationPrefix}
                required
                inputElementRenderer={(props) => <Dropdown {...props} options={affectedAreas} />}
              />
            </FormRow>
            <FormRow>
              <InputField<DefectNotice, string>
                fieldPath="description"
                translationPrefix={translationPrefix}
                required
                inputElementRenderer={(props) => (
                  <TextAreaElement {...props} placeholder={t('descriptionPlaceholder')} />
                )}
              />
            </FormRow>
            <FormRow>
              <InputField<DefectNotice, FileInputValue>
                translationPrefix={translationPrefix}
                hideLabel
                fieldPath="attachments"
                inputElementRenderer={() => (
                  <>
                    <FormRow>
                      <FileInput onChange={handleFileInputChange} label={t('addImage')} multiple />
                    </FormRow>
                    <FormRow>
                      <FlexColumn padding="14px 0" gap="8px">
                        {files.map((file, i) => (
                          <Row key={i} style={{ alignItems: 'center' }}>
                            <FileIcon style={{ flexShrink: 0 }} />
                            <TextBlock>{file.name}</TextBlock>
                            <ThrashFilledIcon
                              onClick={() => handleRemoveFile(i)}
                              style={{ cursor: 'pointer', marginLeft: '8px' }}
                            />
                          </Row>
                        ))}
                      </FlexColumn>
                    </FormRow>
                  </>
                )}
              />
            </FormRow>
            <FormRow>
              <InputField<DefectNotice, boolean>
                fieldPath="masterKeyAccessAllowed"
                translationPrefix={translationPrefix}
                hideLabel
                inputElementRenderer={({ value, id, onChange }) => (
                  <FlexColumn gap="12px">
                    {t('masterKeyAccessAllowed')}
                    <RadioGroup<boolean>
                      id={id}
                      name="masterKeyAccessAllowed"
                      selectedValue={value}
                      onChange={onChange}
                      options={[
                        { label: tCommon('yes'), value: true },
                        { label: tCommon('no'), value: false },
                      ]}
                    />
                  </FlexColumn>
                )}
              />
            </FormRow>
            <FormRow>
              <InputField<DefectNotice, boolean>
                fieldPath="hasPetsInApartment"
                translationPrefix={translationPrefix}
                hideLabel
                inputElementRenderer={({ value, id, onChange }) => (
                  <FlexColumn gap="12px">
                    {t('hasPetsInApartment')}
                    <RadioGroup<boolean>
                      id={id}
                      name="hasPetsInApartment"
                      selectedValue={value}
                      onChange={onChange}
                      options={[
                        { label: tCommon('yes'), value: true },
                        { label: tCommon('no'), value: false },
                      ]}
                    />
                  </FlexColumn>
                )}
              />
            </FormRow>
            {hasPetsInApartment && (
              <FormRow>
                <InputField<DefectNotice, string>
                  fieldPath="petsDescription"
                  translationPrefix={translationPrefix}
                  inputElementRenderer={(props) => <TextAreaElement {...props} placeholder={t('petsDescription')} />}
                />
              </FormRow>
            )}
            {submitFailed && <FormError message={tForms('error.general')} />}
            <ButtonContainer>
              <Button primary icon="send2" type="submit" id="send" disabled={isBusy}>
                {tForms('send')}
              </Button>
            </ButtonContainer>
          </Form>
        </>
      )}
    </FormPageContainer>
  );
};
